import { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { Outlet } from 'react-router-dom';
import { Box } from '@mui/material';

import Header from 'components/Header/Header';
import { Sidebar } from 'components/Sidebar/Sidebar';
import {
  useOwnProfileQuery,
  useProfileHeartbeatMutation,
  useProfileHeartbeatMutationReactivate,
} from '@common/cache';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { theme } from '@store';
import {
  HEARTBEAT_PERIOD,
  HEARTBEAT_PERIOD_VARIATION,
} from '@common/types/constants.ts';
import { isDev } from '@common/types/utilities.ts';
import { StatusBanner } from '@components';
import { t } from 'i18next';
import useUserStore from 'store/user.store';
import * as Sentry from '@sentry/react';

export const AppLayout = () => {
  const profileHeartbeatMutation = useProfileHeartbeatMutation();
  const profileHeartbeatMutationReactivate =
    useProfileHeartbeatMutationReactivate();
  const profile = useOwnProfileQuery();
  const { user } = useUserStore();

  const lastActivityRef = useRef<number>(new Date().getTime());

  useEffect(() => {
    if (profile.data?.activity_status === 'active') {
      profileHeartbeatMutation.mutateAsync().catch(err => {
        toast.error(formatApiError(err));
      });
    }

    const markActive = async () => {
      if (Date.now() - lastActivityRef.current < 1_000) return;
      if (profileHeartbeatMutationReactivate.isPending) return;
      lastActivityRef.current = Date.now();
      if (
        profile.data?.activity_status === 'away' ||
        profile.data?.activity_status === 'offline'
      ) {
        try {
          await profileHeartbeatMutationReactivate.mutateAsync();
        } catch (err) {
          toast.error(formatApiError(err));
        }
      }
    };

    window.addEventListener('mousemove', markActive);
    window.addEventListener('keydown', markActive);
    const heartbeatPeriod = Math.round(
      HEARTBEAT_PERIOD + Math.random() * HEARTBEAT_PERIOD_VARIATION,
    );
    const interval = setInterval(async () => {
      const sinceLast = Date.now() - lastActivityRef.current;
      if (
        sinceLast < heartbeatPeriod &&
        profile.data?.activity_status === 'active'
      ) {
        try {
          await profileHeartbeatMutation.mutateAsync();
        } catch (err) {
          toast.error(formatApiError(err));
        }
      }
    }, heartbeatPeriod);

    return () => {
      clearInterval(interval);
      window.removeEventListener('mousemove', markActive);
      window.removeEventListener('keydown', markActive);
    };
  }, [profile.data?.activity_status]);

  useEffect(() => {
    if (user) {
      Sentry.setUser({
        id: user.id,
        email: user.email,
        username: `${user.first_name} ${user.last_name}`,
      });
    }
  }, [user]);

  const isStaging = isDev();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
        position: 'relative',
        height: '100%',
      }}
    >
      {isStaging && (
        <StatusBanner text={t('general.isStaging')} variant='error' />
      )}
      <Header />
      <Box
        sx={{
          display: 'flex',
          height: '100%',
          overflow: 'hidden',
          bgcolor: theme => theme.palette.grey[100],
        }}
      >
        <Sidebar />
        <Box
          sx={{
            height: '100%',
            width: '100%',
            paddingTop: '24px',
            paddingX: '80px',
            overflowY: 'scroll',
            [theme.breakpoints.down('lg')]: {
              paddingX: '37px',
            },
            [theme.breakpoints.down('md')]: {
              paddingX: '24px',
            },
          }}
        >
          <Box
            sx={{
              maxWidth: '1920px',
              margin: '0 auto',
              boxSizing: 'border-box',
              width: '100%',
            }}
          >
            <Outlet />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
